import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@coin/shared/core/util-environments';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, Observable, throwError } from 'rxjs';
import { WorkflowType } from '@coin/shared/core/util-enums';
import { LoadingService } from '@coin/shared/core/data-access';
import { CoinLetterTemplate } from '@coin/shared/cms/util';
import { PaginatedResult, Workflow } from '@coin/shared/core/util-models';
import { HeadcountSettingsWorkflowTemplateTypeEnum } from '../enums/headcount-settings-request-state.enum';
import { HeadcountApprovalAdminSettingsResponse } from '../interfaces/headcount-approval-admin-settings.model';

@Injectable({
  providedIn: 'root'
})
export class HeadcountApprovalAdminSettingsService {
  private httpClient = inject(HttpClient);
  private loadingService = inject(LoadingService);
  private toast = inject(ToastrService);

  public getAdminSettings(): Observable<HeadcountApprovalAdminSettingsResponse> {
    return this.httpClient
      .get<HeadcountApprovalAdminSettingsResponse>(
        `${environment.api.baseUrl}/admin/api/v1/master/headcount-approval/admin-settings`
      )
      .pipe(this.loadingService.withLoadingScreen);
  }

  public updateAdminSettings(body: HeadcountApprovalAdminSettingsResponse): Observable<ArrayBuffer> {
    return this.httpClient
      .put<ArrayBuffer>(`${environment.api.baseUrl}/admin/api/v1/master/headcount-approval/admin-settings`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error updating settings. ${res?.error?.reason || 'Unknown'} `);
          return throwError(`Error updating settings. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public getWorkflows(workflowType?: WorkflowType): Observable<Workflow[]> {
    const filter = workflowType ? `?category=${workflowType}` : '';
    return this.httpClient
      .get<PaginatedResult<Workflow>>(`${environment.api.baseUrl}/admin/api/v1/master/letters/workflows${filter}`)
      .pipe(
        map(data => data?.content),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error getting workflows. ${res?.error?.reason || 'Unknown'} `);
          return throwError(`Error getting workflows. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public getFilteredWorkflowTemplates(
    type: HeadcountSettingsWorkflowTemplateTypeEnum,
    workflowId: string
  ): Observable<CoinLetterTemplate[]> {
    const url = workflowId
      ? `/admin/api/v1/master/letters/workflows/${workflowId}/templates?type=${type}`
      : `/admin/api/v1/master/letters/workflows/templates?type=${type}`;
    return this.httpClient.get<PaginatedResult<CoinLetterTemplate>>(environment.api.baseUrl + url).pipe(
      map(data => data?.content),
      catchError((res: HttpErrorResponse) => {
        this.toast.error(`Error getting templates. ${res?.error?.reason || 'Unknown'} `);
        return throwError(`Error getting templates. ${res?.error?.reason || 'Unknown'} `);
      })
    );
  }
}
